/**
 * 受鉴权文件下载 —— **端侧触发下载的唯一实现**（C 域流水导出 / N 域备份导出**共用**）
 *
 * 依据：N 域 PRD **§5.4.1**（导出两档制）· C 域 PRD **§5.1.11**（导出永久免费）·
 * **§6.4 铁律 3**（导出能力实现唯一在 C 域流水侧 / N 域只放入口）。
 *
 * ## 🔴 为什么需要这个文件（一个按钮点下去其实有三步）
 *
 * 导出的响应是**文件流**（`Content-Disposition: attachment`），而请求必须带
 * `Authorization` + `X-Ledger-Id` ⇒ **不能用** `<a href="/api/...">` 直接跳（浏览器不会带这两个头，
 * 结果是 401/403）。⇒ 正确形态是：`fetch` → `blob` → 临时 `<a download>` 点击。
 * 这三步在**两个入口**（设置页 / 流水页）都一样 ⇒ 必须**只有一处实现**。
 *
 * ## 🔴 文件名以**服务端响应头为权威**
 *
 * 服务端已经用共享层的 `exportFileName()` / `backupFileName()`（唯一来源）拼好了名字并写在
 * `Content-Disposition` 里 ⇒ 端侧**解析它**即可，**不再自己拼一次日期串**
 * （自己拼 = 同一份命名规则的第二处实现，且两处会因时区/格式漂移）。
 * `fallbackFileName` 只在响应头缺失/不可解析时兜底 —— 那种情况本身是异常，会走运维排查。
 *
 * ## ⚠️ 本文件的**动作**部分不可静态断言（诚实登记）
 *
 * `URL.createObjectURL` / `document.createElement` / `a.click()` 都需要真浏览器与 layout
 * （端侧基座是 `react-dom/server`，无 DOM、不跑 effect —— 铁律 43）⇒
 * 单测只覆盖**能证的**：{@link fileNameFromDisposition} 的解析规则。
 * "点一下真的下了文件"属**渲染层取证**，不写成假的单测。
 */

import { apiRaw } from './http.js';

/**
 * 从 `Content-Disposition` 里取文件名（**纯函数，可静态断言**）。
 *
 * 兼容两种写法：`filename="x.json"`（本项目服务端用的形态）与 `filename=x.json`（无引号）。
 * 取不到返回 `null`（调用方用兜底名），**不抛** —— 响应头缺失是异常，但不该让整个下载崩掉。
 */
export function fileNameFromDisposition(header: string | null): string | null {
  if (header === null || header === '') return null;
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header)?.[1];
  if (quoted !== undefined && quoted !== '') return quoted;
  // 🔴 裸形态**必须排除引号**：`filename=""`（空值）会被 `[^;]+` 匹配成**字面量 `""`**
  //    ⇒ 下载文件名就成了 `""`（两个引号），而用户期望的是"没有可用名字"（回退兜底名）。
  //    排除 `"` 之后该正则**匹配不到**（要求至少一个字符）⇒ 落到 `null`。
  const bare = /filename\s*=\s*([^";]+)/i.exec(header)?.[1]?.trim();
  if (bare === undefined || bare === '') return null;
  return bare;
}

/** 触发浏览器把某个 blob 存成文件（**只可能在真浏览器里跑**） */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 🔴 **不要立刻 revoke**：部分浏览器在 `click()` 返回时就撤销会让下载**静默失败**。
  //    延后一拍释放（这是"能跑起来"的必要动作，不是什么优化）。
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * 下载一个受鉴权的导出接口（两个入口共用）。
 *
 * @param path 形如 `/api/settings/backup` 或 `/api/transactions/export?from=…&format=csv`
 * @param fallbackFileName 响应头不可用时的兜底文件名
 * @returns 实际使用的文件名（调用方可据此提示"已导出 xxx"）
 */
export async function downloadAuthorized(path: string, fallbackFileName: string): Promise<string> {
  const res = await apiRaw(path);
  const fileName = fileNameFromDisposition(res.headers.get('content-disposition')) ?? fallbackFileName;
  const blob = await res.blob();
  saveBlob(blob, fileName);
  return fileName;
}
